import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  useToast,
  Input,
} from '@caruso/ui';
import { executionsService, ProcessExecution, TaskExecution } from '@/services/executions.service';
import { uploadService } from '@/services/upload.service';
import {
  CheckCircle,
  Clock,
  Camera,
  Upload,
  X,
  Loader2,
  AlertCircle,
  FileCheck,
  Image as ImageIcon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface TaskExecutionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  processId: string;
  processName: string;
}

interface TaskFormData {
  description?: string;
  timeSpent?: number;
  photoUrl?: string;
  photoFile?: File;
  notes?: string;
}

export function TaskExecutionModal({
  open,
  onOpenChange,
  processId,
  processName,
}: TaskExecutionModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [execution, setExecution] = useState<ProcessExecution | null>(null);
  const [taskForms, setTaskForms] = useState<Record<string, TaskFormData>>({});
  const [uploadingPhotos, setUploadingPhotos] = useState<Record<string, boolean>>({});
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Start execution when modal opens
  const startExecutionMutation = useMutation({
    mutationFn: () => executionsService.startExecution({ processId }),
    onSuccess: (data) => {
      setExecution(data);
      // Select first pending task
      const firstPending = data.tasks.find((t) => t.status === 'pending');
      if (firstPending) {
        setSelectedTaskId(firstPending.task.id);
      }
    },
    onError: (error: any) => {
      console.error('Erro ao iniciar execução:', error);
      toast({
        title: 'Erro ao iniciar execução',
        description: error.response?.data?.message || error.message || 'Ocorreu um erro ao iniciar a execução',
        variant: 'destructive',
      });
      // NÃO fecha o modal automaticamente - deixa o usuário ver o erro e fechar manualmente
      // onOpenChange(false);
    },
  });

  // Complete task mutation
  const completeTaskMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: any }) =>
      executionsService.completeTask(execution!.id, taskId, data),
    onSuccess: (updatedTask) => {
      if (!execution) return;

      // Update execution state
      const updatedTasks = execution.tasks.map((t) =>
        t.task.id === updatedTask.task.id ? updatedTask : t
      );
      setExecution({ ...execution, tasks: updatedTasks });

      // Clear form for this task
      setTaskForms((prev) => {
        const newForms = { ...prev };
        delete newForms[updatedTask.task.id];
        return newForms;
      });

      // Select next pending task
      const nextPending = updatedTasks.find(
        (t) => t.status === 'pending' && t.task.order > updatedTask.task.order
      );
      if (nextPending) {
        setSelectedTaskId(nextPending.task.id);
      }

      toast({
        title: 'Tarefa completada!',
        description: `${updatedTask.task.name} foi marcada como concluída.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao completar tarefa',
        description: error.response?.data?.message || 'Ocorreu um erro',
        variant: 'destructive',
      });
    },
  });

  // Complete execution mutation
  const completeExecutionMutation = useMutation({
    mutationFn: () => executionsService.completeExecution(execution!.id, {}),
    onSuccess: () => {
      toast({
        title: 'Execução finalizada!',
        description: `O processo "${processName}" foi concluído com sucesso.`,
      });
      queryClient.invalidateQueries({ queryKey: ['my-activities'] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao finalizar execução',
        description: error.response?.data?.message || 'Ocorreu um erro',
        variant: 'destructive',
      });
    },
  });

  // Start execution when modal opens
  useEffect(() => {
    if (open && !execution) {
      startExecutionMutation.mutate();
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setExecution(null);
      setTaskForms({});
      setSelectedTaskId(null);
      setUploadingPhotos({});
    }
  }, [open]);

  const handlePhotoUpload = async (taskId: string, file: File) => {
    try {
      setUploadingPhotos((prev) => ({ ...prev, [taskId]: true }));

      const uploadResult = await uploadService.uploadImage(file);

      setTaskForms((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          photoUrl: uploadResult.url,
          photoFile: file,
        },
      }));

      toast({
        title: 'Foto enviada!',
        description: 'A foto foi carregada com sucesso.',
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao enviar foto',
        description: error.response?.data?.message || 'Ocorreu um erro',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhotos((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const handleCompleteTask = (task: TaskExecution) => {
    const formData = taskForms[task.task.id] || {};

    // Validate required photo
    if (task.task.photoRequired && !formData.photoUrl) {
      toast({
        title: 'Foto obrigatória',
        description: 'Esta tarefa requer uma foto para ser concluída.',
        variant: 'destructive',
      });
      return;
    }

    completeTaskMutation.mutate({
      taskId: task.task.id,
      data: {
        description: formData.description,
        timeSpent: formData.timeSpent,
        photoUrl: formData.photoUrl,
        notes: formData.notes,
      },
    });
  };

  const handleCompleteExecution = () => {
    const pendingTasks = execution?.tasks.filter((t) => t.status === 'pending') || [];

    if (pendingTasks.length > 0) {
      toast({
        title: 'Tarefas pendentes',
        description: `Ainda existem ${pendingTasks.length} tarefa(s) pendente(s).`,
        variant: 'destructive',
      });
      return;
    }

    completeExecutionMutation.mutate();
  };

  const updateTaskForm = (taskId: string, field: keyof TaskFormData, value: any) => {
    setTaskForms((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
  };

  const pendingTasksCount = execution?.tasks.filter((t) => t.status === 'pending').length || 0;
  const completedTasksCount = execution?.tasks.filter((t) => t.status === 'completed').length || 0;
  const totalTasksCount = execution?.tasks.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif text-brand-gold">
            Executar: {processName}
          </DialogTitle>
          <DialogDescription>
            Complete todas as tarefas do processo. Campos obrigatórios estão marcados.
          </DialogDescription>
        </DialogHeader>

        {startExecutionMutation.isPending ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-12 h-12 text-brand-gold animate-spin mb-4" />
            <p className="text-brand-ivory/60">Iniciando execução...</p>
          </div>
        ) : startExecutionMutation.isError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
            <h3 className="text-xl font-semibold text-brand-gold mb-2">
              Erro ao Iniciar Execução
            </h3>
            <p className="text-brand-ivory/60 text-center mb-6 max-w-md">
              {(startExecutionMutation.error as any)?.response?.data?.message ||
               (startExecutionMutation.error as any)?.message ||
               'Não foi possível iniciar a execução deste processo.'}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Fechar
              </Button>
              <Button
                onClick={() => startExecutionMutation.mutate()}
              >
                Tentar Novamente
              </Button>
            </div>
          </div>
        ) : execution ? (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-brand-ivory/60">
                  Progresso: {completedTasksCount} de {totalTasksCount} tarefas
                </span>
                <span className="font-semibold text-brand-gold">
                  {Math.round((completedTasksCount / totalTasksCount) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-brand-smoke/50 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-brand-gold"
                  initial={{ width: 0 }}
                  animate={{ width: `${(completedTasksCount / totalTasksCount) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            {/* Tasks List */}
            <div className="space-y-4">
              {execution.tasks.map((task, index) => {
                const formData = taskForms[task.task.id] || {};
                const isSelected = selectedTaskId === task.task.id;
                const isCompleted = task.status === 'completed';

                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isCompleted
                        ? 'bg-green-500/10 border-green-500/30'
                        : isSelected
                        ? 'bg-brand-gold/10 border-brand-gold'
                        : 'bg-brand-smoke/30 border-brand-gold/20'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isCompleted
                              ? 'bg-green-500/20'
                              : 'bg-brand-gold/20'
                          }`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <span className="text-brand-gold font-bold">
                              {task.task.order + 1}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex-1 space-y-3">
                        <div>
                          <h4 className="text-lg font-medium text-brand-ivory flex items-center gap-2">
                            {task.task.name}
                            {task.task.photoRequired && (
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                                <Camera className="w-3 h-3 inline mr-1" />
                                Foto obrigatória
                              </span>
                            )}
                          </h4>
                          {task.task.description && (
                            <p className="text-sm text-brand-ivory/60 mt-1">
                              {task.task.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 text-sm text-brand-gold">
                            <Clock className="w-4 h-4" />
                            <span>
                              {task.task.minTime} - {task.task.maxTime}
                            </span>
                          </div>
                        </div>

                        {!isCompleted && isSelected && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="space-y-3 pt-3 border-t border-brand-gold/20"
                          >
                            {/* Description */}
                            <div>
                              <label className="block text-sm font-medium text-brand-ivory mb-1">
                                Descrição do que foi executado
                              </label>
                              <textarea
                                value={formData.description || ''}
                                onChange={(e) =>
                                  updateTaskForm(task.task.id, 'description', e.target.value)
                                }
                                placeholder="Descreva brevemente o que foi feito..."
                                rows={2}
                                className="w-full px-3 py-2 bg-brand-smoke border border-brand-gold/20 rounded-md text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold resize-none"
                              />
                            </div>

                            {/* Time Spent */}
                            <div>
                              <label className="block text-sm font-medium text-brand-ivory mb-1">
                                Tempo gasto (minutos)
                              </label>
                              <Input
                                type="number"
                                min="0"
                                value={formData.timeSpent || ''}
                                onChange={(e) =>
                                  updateTaskForm(
                                    task.task.id,
                                    'timeSpent',
                                    e.target.value ? parseInt(e.target.value) : undefined
                                  )
                                }
                                placeholder="Ex: 30"
                                className="w-full"
                              />
                            </div>

                            {/* Photo Upload */}
                            <div>
                              <label className="block text-sm font-medium text-brand-ivory mb-1">
                                Foto {task.task.photoRequired && '(obrigatório)'}
                              </label>
                              {formData.photoUrl ? (
                                <div className="relative">
                                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-3">
                                    <ImageIcon className="w-5 h-5 text-green-400" />
                                    <div className="flex-1">
                                      <p className="text-sm text-green-400 font-medium">
                                        Foto enviada com sucesso
                                      </p>
                                      <p className="text-xs text-brand-ivory/60">
                                        {formData.photoFile?.name}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => updateTaskForm(task.task.id, 'photoUrl', undefined)}
                                      className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                    >
                                      <X className="w-4 h-4 text-red-400" />
                                    </button>
                                  </div>
                                  <img
                                    src={formData.photoUrl}
                                    alt="Preview"
                                    className="mt-2 max-h-48 rounded-lg border border-brand-gold/20"
                                  />
                                </div>
                              ) : (
                                <div className="relative">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handlePhotoUpload(task.task.id, file);
                                      }
                                    }}
                                    className="hidden"
                                    id={`photo-${task.task.id}`}
                                    disabled={uploadingPhotos[task.task.id]}
                                  />
                                  <label
                                    htmlFor={`photo-${task.task.id}`}
                                    className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                      uploadingPhotos[task.task.id]
                                        ? 'border-brand-gold/50 bg-brand-gold/5 cursor-wait'
                                        : 'border-brand-gold/30 hover:border-brand-gold hover:bg-brand-gold/5'
                                    }`}
                                  >
                                    {uploadingPhotos[task.task.id] ? (
                                      <>
                                        <Loader2 className="w-5 h-5 text-brand-gold animate-spin" />
                                        <span className="text-sm text-brand-gold">
                                          Enviando foto...
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="w-5 h-5 text-brand-gold" />
                                        <span className="text-sm text-brand-gold">
                                          Clique para enviar foto
                                        </span>
                                      </>
                                    )}
                                  </label>
                                </div>
                              )}
                            </div>

                            {/* Notes */}
                            <div>
                              <label className="block text-sm font-medium text-brand-ivory mb-1">
                                Observações (opcional)
                              </label>
                              <textarea
                                value={formData.notes || ''}
                                onChange={(e) =>
                                  updateTaskForm(task.task.id, 'notes', e.target.value)
                                }
                                placeholder="Adicione observações sobre esta tarefa..."
                                rows={2}
                                className="w-full px-3 py-2 bg-brand-smoke border border-brand-gold/20 rounded-md text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold resize-none"
                              />
                            </div>

                            {/* Complete Task Button */}
                            <Button
                              onClick={() => handleCompleteTask(task)}
                              disabled={completeTaskMutation.isPending}
                              className="w-full"
                            >
                              {completeTaskMutation.isPending ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Salvando...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Completar Tarefa
                                </>
                              )}
                            </Button>
                          </motion.div>
                        )}

                        {!isCompleted && !isSelected && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedTaskId(task.task.id)}
                          >
                            Executar
                          </Button>
                        )}

                        {isCompleted && task.notes && (
                          <div className="text-sm text-brand-ivory/60 italic">
                            Obs: {task.notes}
                          </div>
                        )}

                        {isCompleted && task.photoUrl && (
                          <a
                            href={task.photoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-400 hover:underline"
                          >
                            <ImageIcon className="w-4 h-4" />
                            Ver foto anexada
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Complete Execution Button */}
            {pendingTasksCount === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="pt-4 border-t border-brand-gold/20"
              >
                <Button
                  onClick={handleCompleteExecution}
                  disabled={completeExecutionMutation.isPending}
                  size="lg"
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {completeExecutionMutation.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Finalizando...
                    </>
                  ) : (
                    <>
                      <FileCheck className="w-5 h-5 mr-2" />
                      Finalizar Execução do Processo
                    </>
                  )}
                </Button>
              </motion.div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
